import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  TextField,
  Typography,
} from "@mui/material";

const SCOPES = [
  { id: "documents.read", label: "documents.read — consulta de documentos" },
  { id: "documents.write", label: "documents.write — carga de documentos" },
];

function buildBasicHeader(clientId, accessKey) {
  return `Basic ${btoa(`${clientId}:${accessKey}`)}`;
}

export default function AccessTokenCreateModal({ open, onClose, onCreate }) {
  const [label, setLabel] = useState("");
  const [scopes, setScopes] = useState(["documents.read", "documents.write"]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [created, setCreated] = useState(null);

  const basicHeader = useMemo(() => {
    if (!created?.client_id || !created?.access_key) return "";
    return buildBasicHeader(created.client_id, created.access_key);
  }, [created]);

  const toggleScope = (scopeId) => {
    setScopes((prev) =>
      prev.includes(scopeId) ? prev.filter((s) => s !== scopeId) : [...prev, scopeId]
    );
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const result = await onCreate({
        label: label.trim(),
        scopes,
      });
      setCreated(result);
    } catch (err) {
      setError(err.message || "Error al crear token");
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setLabel("");
    setScopes(["documents.read", "documents.write"]);
    setError(null);
    setCreated(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{created ? "Credenciales creadas" : "Crear token de acceso"}</DialogTitle>
      <DialogContent>
        {created ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Alert severity="warning">{created.warning}</Alert>
            <TextField
              label="Client ID"
              fullWidth
              value={created.client_id}
              InputProps={{ readOnly: true, sx: { fontFamily: "monospace" } }}
            />
            <TextField
              label="Access Key"
              fullWidth
              value={created.access_key}
              InputProps={{ readOnly: true, sx: { fontFamily: "monospace" } }}
            />
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Authorization para POST /auth
              </Typography>
              <TextField
                fullWidth
                multiline
                minRows={2}
                value={basicHeader}
                InputProps={{ readOnly: true, sx: { fontFamily: "monospace", fontSize: 12 } }}
              />
            </Box>
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
            {error && <Alert severity="error">{error}</Alert>}
            <TextField
              label="Label"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="ERP Dynamics AX — Producción"
              fullWidth
            />
            <FormGroup>
              {SCOPES.map((scope) => (
                <FormControlLabel
                  key={scope.id}
                  control={
                    <Checkbox
                      checked={scopes.includes(scope.id)}
                      onChange={() => toggleScope(scope.id)}
                    />
                  }
                  label={scope.label}
                />
              ))}
            </FormGroup>
            <Alert severity="info">
              El ERP obtiene un Bearer token llamando a <code>POST /auth</code> con el header Basic
              precalculado. El token dura ~60 minutos.
            </Alert>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {created ? (
          <>
            <Button onClick={() => navigator.clipboard.writeText(basicHeader)}>Copiar Basic</Button>
            <Button variant="contained" onClick={handleClose}>
              Cerrar
            </Button>
          </>
        ) : (
          <>
            <Button onClick={handleClose}>Cancelar</Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={submitting || label.trim().length < 3 || scopes.length === 0}
            >
              Crear token
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
